import { Controller } from "@hotwired/stimulus"
import { clearMarks, paintQuote, scrollBehavior } from "./text_anchor"

/**
 * Resolves a stored text anchor back into the live page and highlights it.
 *
 * This is the piece the future student-highlight feature is built on, so it is
 * written to the anchor model that feature needs rather than to the one this
 * caller happens to want. See docs/highlights.md for the reasoning; the short
 * version is that a pointer into an HTML document that keeps changing has to
 * carry several selectors and try them in order:
 *
 *   1. element id   — fast and exact; OpenStax ids every paragraph. Survives
 *                     any edit *inside* the paragraph.
 *   2. text quote   — find the words. Survives the paragraph being moved,
 *                     renumbered or re-id'd, which is what a re-import does.
 *   3. (text position — not stored yet; it only disambiguates a repeated
 *      quote, and the element id already does that for us.)
 *
 * When none of them resolve we scroll to the element if we have one and paint
 * nothing. We never highlight an approximate match: a highlight over the wrong
 * sentence is worse than no highlight, because the student has no way to know.
 *
 * Two callers:
 *
 * - OpenStax: the quiz's "Review 1.1 →" link is a navigation, arriving as
 *     /books/…/assets/1-1-what-is-sociology?highlight=<quote>#<element-id>
 *   The fragment is a real element id, so a browser with no JavaScript still
 *   lands on the right paragraph — it just doesn't get the mark.
 * - Authored themes (appraisal): the question and its passage are on the same
 *   page and the quiz keeps no state, so a URL jump would reload and wipe the
 *   student's answers. The quiz instead dispatches `chapter-quiz:review` with
 *   the quote; we paint it in place, hand the marks back on `event.detail.marks`
 *   (the quiz hangs its "Back to Q1" button off the last one) and cancel the
 *   event, which tells the quiz to swallow its link's own `#section` fallback. No match → we leave the
 *   event alone and the link jumps to the section heading like a plain anchor.
 *
 * The find/paint logic lives in ./text_anchor so the quiz can locate a quote
 * (to name its section) without painting it.
 */
export default class extends Controller {
  static values = {
    param: { type: String, default: "highlight" },
    // Where to search when the element id is missing or no longer exists.
    // The first match wins: OpenStax pages, then the authored theme wrapper.
    scope: { type: String, default: ".openstax-content, .module-content-wrapper" }
  }

  connect() {
    this.onReview = (event) => {
      const marks = this.show(event.detail?.quote)
      if (!marks) return
      if (event.detail) event.detail.marks = marks
      event.preventDefault()
    }
    this.element.addEventListener("chapter-quiz:review", this.onReview)

    const quote = new URLSearchParams(window.location.search).get(this.paramValue)
    if (!quote) return

    // Let the other controllers on this wrapper finish rebuilding the DOM
    // before we index its text, or we index nodes that are about to be thrown
    // away. Two frames is empirically enough; a miss fails soft anyway.
    requestAnimationFrame(() => requestAnimationFrame(() => this.#apply(quote)))
  }

  disconnect() {
    this.element.removeEventListener("chapter-quiz:review", this.onReview)
  }

  /**
   * In-page entry: replace any current highlight with `quote`, scroll to it
   * and announce it. Returns the marks, or null when the quote isn't there.
   */
  show(quote) {
    const scope = this.#scopeEl()
    clearMarks(scope)

    const marks = paintQuote(scope, quote)
    if (!marks) return null

    marks[0].scrollIntoView({ behavior: scrollBehavior(), block: "center" })
    this.#announce(marks[0])
    return marks
  }

  #apply(quote) {
    const target = this.#elementFromHash()
    const marks = paintQuote(target || this.#scopeEl(), quote) || (target ? null : paintQuote(this.#scopeEl(), quote))

    const landing = marks?.[0] || target
    if (!landing) return

    landing.scrollIntoView({ behavior: scrollBehavior(), block: "center" })
    if (marks?.length) this.#announce(marks[0])
  }

  #scopeEl() {
    return this.element.querySelector(this.scopeValue) || this.element
  }

  #elementFromHash() {
    const id = window.location.hash.slice(1)
    if (!id) return null

    try {
      return this.element.querySelector(`#${CSS.escape(id)}`)
    } catch (_) {
      return null
    }
  }

  // Screen readers get no signal from a <mark>, so name what we jumped to.
  #announce(mark) {
    const note = document.createElement("span")
    note.className = "visually-hidden text-anchor-status"
    note.setAttribute("role", "status")
    note.textContent = "Jumped to the passage this answer is based on."
    mark.insertAdjacentElement("afterend", note)
  }
}
